import * as React from 'react';
import strings from '../../strings/strings.json';
import { breakpoints, sizes, theme } from 'theme';
import useGetRequest from 'hooks/useGetRequest';
import OrganisationList from './components/OrganisationList';
import { UserForAdminDashboard } from 'models/UserForAdminDashboard';
import { MainSectionWithGaps } from '../../components/sections/MainSection';
import UsersList from './components/UsersList';
import styled from 'styled-components';
import { Flex, Headline2, Headline4 } from '../../components/Elements';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '../../assets/icons/controls/AddIcon';
import { IconSize } from '../../assets/icons/icon-sizes';
import Notification, { NotificationType } from '../../components/notifiers/Notification';
import AdminBillingList from './components/BillingList';
import TabStructureComponent from 'components/tabs/TabStructureComponent';
import { TabDefinition } from 'components/tabs/Tabs';
import OrganisationIcon from 'assets/icons/navigation/OrganisationIcon';
import FileIcon from 'assets/icons/other/FileIcon';
import Header from 'components/sections/Header';
import Button from 'components/buttons/Button';

type State = {
    addUserSuccess: boolean;
};

export default function AdminDashboardPage(): JSX.Element {
    const navigate = useNavigate();

    const { state } = useLocation();
    const { addUserSuccess } = (state as State) ?? {};

    const {
        data: users,
        loading: usersLoading,
        errors: usersErrors,
        refetchData: refetchUsers,
    } = useGetRequest<UserForAdminDashboard[]>('admin/users');

    const usersSplitByOrganisation: Record<string, UserForAdminDashboard[]> = users
        ? users.reduce((result: Record<string, UserForAdminDashboard[]>, user) => {
              const organisation = user.organisation?.name ?? strings.adminDashboardPage.noOrganisationHeader;

              if (!result[organisation]) {
                  result[organisation] = [];
              }

              result[organisation].push(user);

              return result;
          }, {})
        : {};

    const tabs: TabDefinition[] = [
        {
            id: 'Users',
            name: 'Users',
            icon: OrganisationIcon,
            content: (
                <OrganisationUsersContainer>
                    <UsersSectionHeader>
                        <Headline2>{strings.adminDashboardPage.pageHeading}</Headline2>
                        <ButtonsContainer>
                            <Button
                                title={strings.adminDashboardPage.addUserButton.title}
                                aria={strings.adminDashboardPage.addUserButton.aria}
                                onClick={() => {
                                    navigate('/admin/add-user');
                                }}
                                icon={<AddIcon size={IconSize.SMALL} colour={theme.textColourInverted} />}
                            />
                            <Button
                                title={strings.adminDashboardPage.notifyUsersButton.title}
                                aria={strings.adminDashboardPage.notifyUsersButton.aria}
                                onClick={() => navigate('/admin/notify-users')}
                            />
                        </ButtonsContainer>
                    </UsersSectionHeader>
                    {Object.keys(usersSplitByOrganisation).map((organisation) => {
                        return (
                            <div key={organisation}>
                                <OrganisationHeadline>{organisation}</OrganisationHeadline>
                                <UsersList
                                    users={usersSplitByOrganisation[organisation]}
                                    loading={usersLoading}
                                    errors={usersErrors}
                                    refetchUsers={refetchUsers}
                                />
                            </div>
                        );
                    })}
                </OrganisationUsersContainer>
            ),
        },
        {
            id: 'Organisations',
            name: 'Organisations',
            icon: OrganisationIcon,
            content: <OrganisationList />,
        },
        {
            id: 'Bills',
            name: 'Bills',
            icon: FileIcon,
            content: <AdminBillingList />,
        },
    ];

    return (
        <>
            <Header title={strings.adminDashboardPage.bannerHeading} roleSwitch />
            <MainSectionWithGaps>
                {addUserSuccess && (
                    <Notification
                        notificationType={NotificationType.SUCCESS}
                        title={strings.adminDashboardPage.userAddedSuccessfullyMessage.title}
                        description={strings.adminDashboardPage.userAddedSuccessfullyMessage.description}
                        maxWidth={'60rem'}
                    />
                )}
                <TabStructureComponent tabs={tabs} />
            </MainSectionWithGaps>
        </>
    );
}

const OrganisationUsersContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingLg};
`;

const UsersSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: ${sizes.spacingMd};
    }
`;

const OrganisationHeadline = styled(Headline4)`
    margin-bottom: ${sizes.spacingMd};
`;

const ButtonsContainer = styled(Flex)`
    gap: ${sizes.spacingMd};
`;
